import React from 'react';
import { createPortal } from 'react-dom';

import { TOAST, TOAST_ANIM } from '../../theme/tokens';
import { Icon, type IconName } from '../Icon/Icon';
import { Viewport, Pill, IconWrap, Message } from './Toast.styles';

export type ToastOptions = {
  /** i18n interpolation values for the message copy. */
  placeholders?: Record<string, string | number>;
  /** Optional leading glyph (e.g. 'check' for a success confirmation). */
  icon?: IconName;
};

export type ToastData = ToastOptions & {
  /** The (translatable) copy to display. */
  message: string;
  /** Bumped on every show so re-triggering with identical copy still re-fires. */
  token: number;
};

export type ToastProps = {
  /** The toast to show, or `null` to hide it. Produce it with `useToast`. */
  toast: ToastData | null;
  /** Fired once the toast has fully animated out and unmounted. */
  onDismiss?: () => void;
  className?: string;
};

/**
 * A subtle, elevated "snackbar" pill that slides up from the bottom-centre to
 * confirm an action, then animates out. Presentational and self-animating —
 * drive it declaratively from the `useToast` hook: `<Toast toast={toast} />`.
 *
 * It renders into a portal on `document.body`, so it floats above all page
 * chrome regardless of where it is mounted in the tree. While the pill is shown
 * it keeps rendering its last content during the exit transition (the area's
 * keep-mounted-then-drop pattern), and a fresh `toast` mid-exit cancels the
 * dismissal and slides it back in. Mirrors Figma's notification: a dark pill,
 * near-white copy, a soft shadow, no border chrome.
 */
export const Toast = ({ toast, onDismiss, className }: ToastProps): React.ReactElement | null => {
  // `content` is the data currently on screen (retained through the exit
  // animation); `on` drives the slide/fade between the offset and resting state.
  const [content, setContent] = React.useState<ToastData | null>(null);
  const [on, setOn] = React.useState(false);

  // Keep the latest onDismiss without retriggering the effect when it changes.
  const onDismissRef = React.useRef(onDismiss);
  onDismissRef.current = onDismiss;
  // Whether a toast is currently on screen — read in the effect via a ref so
  // `content` doesn't have to be a dependency (which would re-fire the effect on
  // every show and double-schedule the enter frame).
  const shownRef = React.useRef(false);
  const exitTimer = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    if (toast) {
      // Show / re-show: cancel any pending dismissal, adopt the content, then
      // flip to the resting state one frame later so the transition plays. If
      // the pill is already resting this is a no-op and the copy swaps in place.
      window.clearTimeout(exitTimer.current);
      shownRef.current = true;
      setContent(toast);
      const raf = requestAnimationFrame(() => setOn(true));
      return () => cancelAnimationFrame(raf);
    }
    if (shownRef.current) {
      // Hidden: animate out, then drop the content once the transition is done.
      setOn(false);
      exitTimer.current = window.setTimeout(() => {
        shownRef.current = false;
        setContent(null);
        onDismissRef.current?.();
      }, TOAST_ANIM.EXIT_MS);
    }
    return undefined;
  }, [toast]);

  // Clear a pending exit timer if the component unmounts mid-animation.
  React.useEffect(() => () => window.clearTimeout(exitTimer.current), []);

  if (!content) return null;

  return createPortal(
    <Viewport className={className}>
      {/* role="status" is itself an aria-live="polite" + aria-atomic region, so
          the copy is announced when the pill appears — no separate live region. */}
      <Pill $on={on} role="status">
        {content.icon ? (
          <IconWrap>
            <Icon name={content.icon} size={16} color={TOAST.icon} />
          </IconWrap>
        ) : null}
        <Message text={content.message} tag="span" placeholders={content.placeholders} />
      </Pill>
    </Viewport>,
    document.body,
  );
};

export default Toast;
